/**
 * Product Card Custom Element
 * 
 * Reuses logic from algolia-product-helpers.js for consistent behavior
 * across Algolia and Liquid product cards.
 */

/**
 * Global Klaviyo Helper Functions
 * Shared between product-card.js and algolia-product-helpers.js
 */
window.klaviyoProductHelpers = {
  /**
   * Check if Klaviyo button exists in the DOM
   * Klaviyo appends this button dynamically
   */
  hasKlaviyoButton(cardElement) {
    if (!cardElement) return false;
    const klaviyoContainer = cardElement.querySelector('.klaviyo-product-container');
    if (!klaviyoContainer) return false;
    
    const klaviyoButton = klaviyoContainer.querySelector('.product-card__klaviyo-button');
    return klaviyoButton !== null;
  },

  /**
   * Check if product has any available variants based on DOM
   */
  hasAvailableVariants(cardElement) {
    if (!cardElement) return false;
    const variantButtons = cardElement.querySelectorAll('.product-card__size-button');
    if (!variantButtons.length) return false;
    
    return Array.from(variantButtons).some(btn => btn.dataset.available === 'true');
  },

  /**
   * Show Klaviyo button, hide default ATC
   * Always hides default ATC for OOS products, even if Klaviyo button hasn't loaded yet
   */
  showKlaviyoHideDefault(cardElement) {
    if (!cardElement) return;
    
    const klaviyoContainer = cardElement.querySelector('.klaviyo-product-container');
    const atcButton = cardElement.querySelector('.product-card__atc-button');
    
    if (!klaviyoContainer) return;

    // Always show Klaviyo container and hide default button for OOS products
    // Even if Klaviyo button hasn't loaded yet (better than showing both buttons)
    klaviyoContainer.classList.remove('hidden');
    if (atcButton) {
      atcButton.classList.add('hidden');
    }
  },

  /**
   * Show default ATC button, hide Klaviyo
   */
  showDefaultHideKlaviyo(cardElement) {
    if (!cardElement) return;
    
    const klaviyoContainer = cardElement.querySelector('.klaviyo-product-container');
    const atcButton = cardElement.querySelector('.product-card__atc-button');
    
    if (!klaviyoContainer) return;

    // Show default ATC, hide Klaviyo
    if (atcButton) {
      atcButton.classList.remove('hidden');
    }
    klaviyoContainer.classList.add('hidden');
  },

  /**
   * Update button visibility when variant is selected by user
   * @param {HTMLElement} cardElement - The product card element
   * @param {boolean} isVariantAvailable - Whether the selected variant is available
   */
  updateKlaviyoVisibility(cardElement, isVariantAvailable) {
    if (!cardElement) return;
    
    const klaviyoContainer = cardElement.querySelector('.klaviyo-product-container');
    if (!klaviyoContainer) return;

    // Check if this is a product with all variants unavailable
    const hasAvailableVariants = this.hasAvailableVariants(cardElement);

    if (!hasAvailableVariants) {
      // All variants unavailable - don't change state on variant selection
      // Keep Klaviyo shown, default hidden
      return;
    }

    // At least one variant available - toggle based on selected variant
    if (isVariantAvailable) {
      // Available variant selected - show default ATC, hide Klaviyo
      this.showDefaultHideKlaviyo(cardElement);
    } else {
      // Unavailable variant selected - show Klaviyo, hide default ATC
      this.showKlaviyoHideDefault(cardElement);
    }
  }
};

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.productData = null;
    this.selectedVariantId = null;
    this.isInitialized = false;
  }

  connectedCallback() {
    if (this.isInitialized) return;
    
    // Parse variant data from JSON script tag
    const variantDataScript = this.querySelector('.product-card__variant-data');
    if (variantDataScript) {
      try {
        this.productData = JSON.parse(variantDataScript.textContent);
      } catch (e) {
        console.error('Failed to parse product variant data', e);
        return;
      }
    }

    // Initialize the card
    this.init();
    this.isInitialized = true;
  }

  init() {
    // Variant buttons, mobile size links, ATC initial state, OOS overlay, and
    // initial Klaviyo visibility are all pre-rendered by the snippet now. JS
    // only wires interactivity: variant selection click handler, price tooltip
    // updates, and a late Klaviyo sync once Klaviyo's BIS button finishes loading.
    this.setupEventListeners();

    setTimeout(() => {
      this.initKlaviyoIntegration();
    }, 1000);

    // Single-variant cards have the variant pre-selected by Liquid (active
    // class on the size button, ATC enabled, hidden form input value set). For
    // multi-variant cards, selection happens on user click.
    if (this.productData?.variants?.length === 1) {
      this.selectedVariantId = String(this.productData.variants[0].id);
    }
  }

  /**
   * Setup all event listeners
   */
  setupEventListeners() {
    // Variant button click handlers
    this.addEventListener('click', (e) => {
      // Variant selection
      if (e.target.classList.contains('product-card__size-button')) {
        e.preventDefault();
        e.stopPropagation();
        this.handleVariantSelection(e.target);
        return;
      }

      // ATC button click: the button is type=submit referencing the form via
      // form="…". Browser submits the form, which triggers the surrounding
      // <product-form>'s submit handler. We only stop event propagation so the
      // wrapping anchor doesn't navigate to the PDP. NO preventDefault — that
      // would block the submit.
      if (e.target.closest('.product-card__atc-button')) {
        e.stopPropagation();
        return;
      }
    });

    // On product template, the Klaviyo "Notify Me" span opens the PDP in a new tab
    // with the currently selected variant + highlight_klaviyo param, without ever
    // storing that param in the card link itself.
    if (document.body.classList.contains('template-product')) {
      const klaviyoSpan = this.querySelector('.product-card__klaviyo-button');
      if (klaviyoSpan) {
        klaviyoSpan.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const cardLink = this.querySelector('.product-card__link');
          if (!cardLink) return;
          const url = new URL(cardLink.href, location.origin);
          url.searchParams.set('highlight_klaviyo', '1');
          window.open(url.toString(), '_blank', 'noopener');
        });
      }
    }

    // Prevent default for size mobile links when card is clicked
    const sizeLinks = this.querySelectorAll('.product-card__size-mobile-link');
    sizeLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    });
  }

  /**
   * Handle variant selection
   */
  handleVariantSelection(button) {
    const addToCartText = this.getLocale('add_to_cart', 'Add to Cart');
    const outOfStockText = this.getOutOfStockText();
    const comingSoonText = this.getComingSoonText();

    // Remove active class from all buttons
    const allButtons = this.querySelectorAll('.product-card__size-button');
    allButtons.forEach(btn => btn.classList.remove('active'));

    // Add active class to clicked button
    button.classList.add('active');

    const variantId = button.dataset.variantId;
    const isAvailable = button.dataset.available === 'true';
    this.selectedVariantId = variantId;
    this.writeVariantToForm(variantId);

    // Update ATC button
    const atcButton = this.querySelector('.product-card__atc-button');
    const productPriceContainer = this.querySelector('.product-card__price');
    if (atcButton) {
      atcButton.dataset.variantId = variantId;
      atcButton.dataset.variantAvailable = isAvailable.toString();

      if (productPriceContainer) {
        if (button.dataset.tooltipPrice) {
          this.updatePriceContainer(productPriceContainer, button, false);
        }
      }

      if (isAvailable) {
        this.setAtcText(atcButton, addToCartText);
        atcButton.disabled = false;
      } else {
        const hasComingSoonTag = this.dataset.tags && this.dataset.tags.includes('coming_soon');
        this.setAtcText(atcButton, hasComingSoonTag ? comingSoonText : outOfStockText);
        atcButton.disabled = true;
      }

      window.klaviyoProductHelpers.updateKlaviyoVisibility(this, isAvailable);

      // Keep the card link in sync with the selected variant so clicking
      // anywhere on the card opens the PDP with that variant pre-selected.
      const cardLink = this.querySelector('.product-card__link');
      if (cardLink) {
        const url = new URL(cardLink.href, location.origin);
        url.searchParams.set('variant', variantId);
        cardLink.href = url.toString();
      }
    }
    // Update price container
  
  }

  /**
   * Set the ATC button's visible label without nuking its child elements
   * (loading spinner, etc.). The label lives in .product-card__atc-button-text
   * so we update that span's textContent instead of the button's, which would
   * remove the spinner.
   */
  setAtcText(button, text) {
    if (!button) return;
    const span = button.querySelector('.product-card__atc-button-text');
    if (span) span.textContent = text;
    else button.textContent = text;
  }

  /**
   * Sync the selected variant id into the form's hidden <input name="id"> so
   * that submitting the form (triggered by the ATC button via form="…") sends
   * the right variant to /cart/add. The form sits outside the <a> link, as a
   * sibling of <product-card> inside the wrapping <product-form>.
   */
  writeVariantToForm(variantId) {
    if (!variantId) return;
    const hiddenInput = this.querySelector('product-form input[name="id"]')
      || this.querySelector('form input[name="id"]');
    if (hiddenInput) hiddenInput.value = variantId;
  }

  /**
   * Format price with currency
   */
  formatPrice(priceInCents) {
    const price = priceInCents / 100;
    const currency = this.dataset.currency || 'USD';
    const currencyLower = currency.toLowerCase();

    if (currencyLower === 'cad') {
      return `$${price.toFixed(2)} CAD`;
    }

    if (currencyLower === 'usd') {
      return `$${price.toFixed(2)} USD`;
    }

    return `${price.toFixed(2)} ${currency.toUpperCase()}`;
  }

  /**
   * Get locale string from window.algoliaLocales
   */
  getLocale(key, fallback) {
    if (window.algoliaLocales && window.algoliaLocales.product) {
      return window.algoliaLocales.product[key] || fallback;
    }
    return fallback;
  }

  /**
   * Get out of stock text
   */
  getOutOfStockText() {
    return this.getLocale('out_of_stock', 'Out Of Stock');
  }

  /**
   * Get coming soon text
   */
  getComingSoonText() {
    return this.getLocale('coming_soon', 'Coming Soon');
  }
  /**
   * Updates the price container when a variant is selected or deselected
   * @param {HTMLElement} priceContainer - The product card price container
   * @param {HTMLElement|null} variantButton - The variant button that was clicked (null when restoring)
   * @param {boolean} restore - Whether to restore the original prices
   */
  updatePriceContainer(priceContainer, variantButton, restore = false) {
    if (!priceContainer) return;

    // Store original prices on first interaction
    if (!priceContainer.dataset.originalPrice) {
      const currentPriceEl = priceContainer.querySelector('.product-card__price-current');
      const originalPriceEl = priceContainer.querySelector('.product-card__price-original');
      const discountEl = priceContainer.querySelector('.product-card__price-discount:not([class*="badge"])');
      const fromEl = priceContainer.querySelector('.product-card__price-from');
      
      
      if (currentPriceEl) {
        priceContainer.dataset.originalPrice = currentPriceEl.textContent;
      }
      if (originalPriceEl) {
        priceContainer.dataset.originalComparePrice = originalPriceEl.textContent;
      }
      if (discountEl) {
        priceContainer.dataset.originalDiscount = discountEl.textContent;
      }
      priceContainer.dataset.hadFrom = fromEl ? 'true' : 'false';
      priceContainer.dataset.hadComparePrice = originalPriceEl ? 'true' : 'false';
    }

    const currentPriceEl = priceContainer.querySelector('.product-card__price-current');
    const originalPriceEl = priceContainer.querySelector('.product-card__price-original');
    const discountEl = priceContainer.querySelector('.product-card__price-discount:not([class*="badge"])');
    const fromEl = priceContainer.querySelector('.product-card__price-from');
    const saleBadgeEl = priceContainer.querySelector('.product-card__price-badge--sale');
    // console.log("🚀 ~ saleBadgeEl:", saleBadgeEl)

    if (restore) {
      // Restore original prices
      if (currentPriceEl && priceContainer.dataset.originalPrice) {
        currentPriceEl.textContent = priceContainer.dataset.originalPrice;
      }

      // Restore or remove "From" label
      if (priceContainer.dataset.hadFrom === 'true') {
        if (!fromEl) {
          const fromLabel = document.createElement('span');
          fromLabel.className = 'product-card__price-from';
          fromLabel.textContent = window.algoliaLocales?.product?.from_label || 'From';
          priceContainer.insertBefore(fromLabel, currentPriceEl);
        } else {
          fromEl.style.display = '';
        }
      }

      // Restore original compare price and discount
      if (priceContainer.dataset.hadComparePrice === 'true') {
        if (originalPriceEl) {
          originalPriceEl.textContent = priceContainer.dataset.originalComparePrice || '';
          originalPriceEl.style.display = '';
        }
        if (discountEl) {
          discountEl.textContent = priceContainer.dataset.originalDiscount || '';
          discountEl.style.display = '';
        }
      } else {
        // Hide compare price and discount if they weren't there originally
        if (originalPriceEl) {
          originalPriceEl.style.display = 'none';
        }
        if (discountEl) {
          discountEl.style.display = 'none';
        }
      }

      return;
    }

    // Update prices based on selected variant
    if (!variantButton) return;

    const variantPrice = variantButton.dataset.tooltipPrice;
    const variantComparePrice = variantButton.dataset.tooltipComparePrice;

    // Update current price
    if (currentPriceEl && variantPrice) {
      currentPriceEl.textContent = variantPrice;
    }

    // Hide "From" label when specific variant is selected
    if (fromEl) {
      fromEl.style.display = 'none';
    }

    // Update compare price and discount
    if (variantComparePrice && variantComparePrice.trim() !== '') {
      // Parse prices to calculate discount
      const parsePrice = (priceStr) => {
        const match = priceStr.match(/[\d,]+\.?\d*/);
        return match ? parseFloat(match[0].replace(',', '')) : 0;
      };

      const currentPrice = parsePrice(variantPrice);
      const comparePrice = parsePrice(variantComparePrice);

      if (comparePrice > currentPrice) {
        // Show compare price
        if (originalPriceEl) {
          originalPriceEl.textContent = variantComparePrice;
          originalPriceEl.style.display = '';
        } else {
          // Create compare price element if it doesn't exist
          const newOriginalPriceEl = document.createElement('span');
          newOriginalPriceEl.className = 'product-card__price-original';
          newOriginalPriceEl.textContent = variantComparePrice;
          currentPriceEl.after(newOriginalPriceEl);
        }

        // Calculate and show discount percentage
        const discountPercent = Math.round(((comparePrice - currentPrice) / comparePrice) * 100);
        if (discountEl) {
          discountEl.textContent = `-${discountPercent}%`;
          discountEl.style.display = '';
        } else {
          // Create discount element if it doesn't exist
          const newDiscountEl = document.createElement('span');
          newDiscountEl.className = 'product-card__price-discount';
          newDiscountEl.textContent = `-${discountPercent}%`;
          const insertAfter = priceContainer.querySelector('.product-card__price-original');
          if (insertAfter) {
            insertAfter.after(newDiscountEl);
          } else {
            currentPriceEl.after(newDiscountEl);
          }
        }
        if (saleBadgeEl) {
          saleBadgeEl.style.display = '';
        }
      } else {
        // Hide compare price and discount if compare price is not greater
        if (originalPriceEl) {
          originalPriceEl.style.display = 'none';
        }
        if (discountEl) {
          discountEl.style.display = 'none';
        }
      }
    } else {
      // No compare price for this variant - hide compare price elements
      if (originalPriceEl) {
        originalPriceEl.style.display = 'none';
      }
      if (discountEl) {
        discountEl.style.display = 'none';
      }
      if (saleBadgeEl) {
        saleBadgeEl.style.display = 'none';
      }
    }
  }

  /**
   * Initialize Klaviyo integration
   * Sets up initial state for Klaviyo back-in-stock button
   * This is called with setTimeout to allow Klaviyo to load its button
   * Exception: On product template, Klaviyo doesn't work for product cards
   */
  initKlaviyoIntegration() {
    const isProductTemplate = document.body.classList.contains('template-product');
    if (isProductTemplate) return; // Skip Klaviyo on product template

    const klaviyoContainer = this.querySelector('.klaviyo-product-container');
    if (!klaviyoContainer) return;

    // Re-apply initial state now that Klaviyo may have loaded
    const hasAvailableVariants = window.klaviyoProductHelpers.hasAvailableVariants(this);
    
    if (!hasAvailableVariants) {
      // All variants unavailable - show Klaviyo, hide default ATC
      window.klaviyoProductHelpers.showKlaviyoHideDefault(this);
    } else {
      // At least one variant available - show default "Choose variant", hide Klaviyo
      window.klaviyoProductHelpers.showDefaultHideKlaviyo(this);
    }
  }

  /**
   * Check if product has any available variants (from product data)
   * Used during initial rendering before DOM buttons exist
   */
  hasAvailableVariants() {
    if (!this.productData || !this.productData.variants) return false;
    
    return this.productData.variants.some(variant => {
      return variant.available === 'true' || variant.available === true;
    });
  }

}

// Register custom element
if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

